<!--
@component
NextMatchesWidget

Visual-only matches widget. Renders the list of matches it's handed,
trimmed by `maxMatches` (defaults to a responsive 1 / 2 / 3 cards
based on viewport width). The widget is intentionally dumb about
filtering — callers are responsible for handing over the right list
(e.g. live + upcoming for the global header, the team's full schedule
for an embed, etc.).
-->

<script lang="ts">
  import { onMount } from 'svelte';
  import Match from './Match.svelte';
  import type { ContestedMatch } from '$lib/@types/opta';

  // Breakpoints kept in sync with the SCSS media queries below.
  // Tuned for the header-rail context, which competes with the logo
  // and nav for horizontal space:
  //   - desktop (> tablet): 3 matches
  //   - tablet  (<= 600px): 2 matches
  //   - mobile  (<= 375px): 1 match
  const BREAKPOINT_NARROW = 600;
  const BREAKPOINT_NARROWER = 375;
  const MATCH_WIDTH_PX = 175;

  type Props = {
    /**
     * List of matches to render. The widget renders them in order and
     * doesn't filter or sort — pre-compute the list (live + upcoming,
     * a team's schedule, etc.) before passing it in.
     */
    matches: ContestedMatch[];
    /**
     * Maximum number of matches to display. When omitted, falls back
     * to a responsive default (1 / 2 / 3 cards depending on viewport
     * width). Useful for embeds that want to surface more upcoming
     * fixtures, e.g. all of a team's group-stage games.
     */
    maxMatches?: number;
  };

  const { matches, maxMatches }: Props = $props();

  // Responsive default card count, driven by `matchMedia` rather than
  // tracking `windowWidth` on every resize. This keeps the JS
  // thresholds aligned with the SCSS media queries and only re-runs
  // when a breakpoint is actually crossed.
  //
  // We initialise *synchronously* on the client by reading `matchMedia`
  // in the `$state` initialiser, so the very first paint already has
  // the correct count.
  //
  // **SSR defaults to `1`, not `3`** — because we can't know the
  // viewport server-side, and growing 1 → N on hydration is visually
  // fine (a card appears next to an existing one), while shrinking N
  // → fewer would feel like content disappearing. On wide viewports
  // the client will bump this to 2 or 3 on hydration; on narrow
  // viewports it stays at 1.
  const initialResponsiveCount = () => {
    if (typeof window === 'undefined') return 1;
    if (window.matchMedia(`(max-width: ${BREAKPOINT_NARROWER}px)`).matches)
      return 1;
    if (window.matchMedia(`(max-width: ${BREAKPOINT_NARROW}px)`).matches)
      return 2;
    return 3;
  };

  let responsiveCount = $state(initialResponsiveCount());

  onMount(() => {
    const narrow = window.matchMedia(`(max-width: ${BREAKPOINT_NARROW}px)`);
    const narrower = window.matchMedia(`(max-width: ${BREAKPOINT_NARROWER}px)`);

    const update = () => {
      if (narrower.matches) responsiveCount = 1;
      else if (narrow.matches) responsiveCount = 2;
      else responsiveCount = 3;
    };

    update();
    narrow.addEventListener('change', update);
    narrower.addEventListener('change', update);

    return () => {
      narrow.removeEventListener('change', update);
      narrower.removeEventListener('change', update);
    };
  });

  // Honour an explicit `maxMatches` if given, else fall back to the
  // responsive count.
  const count = $derived(maxMatches ?? responsiveCount);

  const matchList = $derived(matches.slice(0, count));

  /**
   * When the caller asks for more matches than fit in a single rail
   * row, switch to a wrapping grid so we don't blow out the embed
   * width. `--cards-per-row` caps the per-row width.
   */
  const cardsPerRow = $derived(Math.min(matchList.length, 3));

  // Stable key for the each block: prefer the match id, fall back to index.
  const keyOf = (m: ContestedMatch, i: number) => m?.matchInfo?.id ?? i;
</script>

{#if matchList.length}
  <div
    class="match-rail-wrapper relative fmy-3 mx-auto flex justify-center"
    class:wrap={matchList.length > 3}
    style="--matchCount:{matchList.length}; --cards-per-row:{cardsPerRow}; --match-width:{MATCH_WIDTH_PX}px;"
  >
    {#each matchList as match, i (keyOf(match, i))}
      <Match {match} />
    {/each}
  </div>
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  // Keep these in sync with the constants in the <script> block.
  $bp-narrow: 600px;
  $bp-narrower: 375px;

  .match-rail-wrapper {
    @include mixins.hard-shadow;
    background-color: var(--theme-colour-muted-grey);
    border-radius: calc(1 * var(--theme-style-border-radius));
    width: calc(var(--matchCount) * var(--match-width, 175px));
    z-index: 1;

    @media (max-width: $bp-narrow) {
      max-width: mixins.$column-width-narrower;
    }
    @media (max-width: $bp-narrower) {
      max-width: #{mixins.$column-width-narrower * 0.5};
    }

    :global(.live-container) {
      position: absolute !important;
      top: -0.75rem;
      left: 50%;
      transform: translateX(-50%);
    }
  }
</style>
